<template>
  <nav
    class="ecx-pagination flex items-center justify-between"
    :class="containerClass"
    aria-label="Pagination"
  >
    <!-- Results info -->
    <div v-if="showInfo" class="text-sm text-subtitle-gray">
      <span v-if="totalItems > 0">
        Mostrando <span class="font-medium text-default-black">{{ startItem }}</span> a
        <span class="font-medium text-default-black">{{ endItem }}</span> de
        <span class="font-medium text-default-black">{{ totalItems }}</span> resultados
      </span>
      <span v-else>Nenhum resultado encontrado</span>
    </div>

    <!-- Pagination controls -->
    <div class="flex items-center gap-1">
      <!-- First page button -->
      <button
        v-if="showFirstLast"
        type="button"
        :disabled="currentPage === 1"
        :class="[buttonBaseClasses, currentPage === 1 ? disabledClasses : hoverClasses]"
        @click="goToPage(1)"
        aria-label="Primeira página"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>

      <!-- Previous button -->
      <button
        type="button"
        :disabled="currentPage === 1"
        :class="[buttonBaseClasses, currentPage === 1 ? disabledClasses : hoverClasses]"
        @click="goToPage(currentPage - 1)"
        aria-label="Página anterior"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <!-- Page numbers -->
      <template v-for="page in visiblePages" :key="page">
        <span
          v-if="page === '...'"
          class="px-2 text-subtitle-gray"
        >
          ...
        </span>
        <button
          v-else
          type="button"
          :class="[
            buttonBaseClasses,
            page === currentPage ? activeClasses : hoverClasses,
          ]"
          :aria-current="page === currentPage ? 'page' : undefined"
          @click="goToPage(page as number)"
        >
          {{ page }}
        </button>
      </template>

      <!-- Next button -->
      <button
        type="button"
        :disabled="currentPage === totalPages"
        :class="[buttonBaseClasses, currentPage === totalPages ? disabledClasses : hoverClasses]"
        @click="goToPage(currentPage + 1)"
        aria-label="Próxima página"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <!-- Last page button -->
      <button
        v-if="showFirstLast"
        type="button"
        :disabled="currentPage === totalPages"
        :class="[buttonBaseClasses, currentPage === totalPages ? disabledClasses : hoverClasses]"
        @click="goToPage(totalPages)"
        aria-label="Última página"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Per page selector -->
    <div v-if="showPerPage" class="flex items-center gap-2">
      <span class="text-sm text-subtitle-gray">Por página:</span>
      <select
        :value="perPage"
        class="rounded-lg border border-grey-200 py-1 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        @change="handlePerPageChange"
      >
        <option v-for="option in perPageOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface PaginationProps {
  /** Página atual (1-indexed) */
  currentPage: number
  /** Total de itens */
  totalItems: number
  /** Itens por página */
  perPage?: number
  /** Opções de itens por página */
  perPageOptions?: number[]
  /** Número de páginas visíveis nos controles */
  visiblePagesCount?: number
  /** Mostrar informações de resultados */
  showInfo?: boolean
  /** Mostrar botões primeira/última página */
  showFirstLast?: boolean
  /** Mostrar seletor de itens por página */
  showPerPage?: boolean
  /** Classes para o container */
  containerClass?: string
}

const props = withDefaults(defineProps<PaginationProps>(), {
  perPage: 10,
  perPageOptions: () => [10, 25, 50, 100],
  visiblePagesCount: 5,
  showInfo: true,
  showFirstLast: false,
  showPerPage: false,
})

const emit = defineEmits<{
  'update:currentPage': [page: number]
  'update:perPage': [perPage: number]
  'page-change': [page: number]
}>()

// Computed
const totalPages = computed(() => Math.ceil(props.totalItems / props.perPage) || 1)
const startItem = computed(() => (props.currentPage - 1) * props.perPage + 1)
const endItem = computed(() => Math.min(props.currentPage * props.perPage, props.totalItems))

const visiblePages = computed(() => {
  const pages: (number | string)[] = []
  const total = totalPages.value
  const current = props.currentPage
  const visible = props.visiblePagesCount

  if (total <= visible) {
    // Show all pages
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    // Calculate range
    const half = Math.floor(visible / 2)
    let start = Math.max(1, current - half)
    let end = Math.min(total, start + visible - 1)

    // Adjust if at the end
    if (end - start + 1 < visible) {
      start = Math.max(1, end - visible + 1)
    }

    // Add first page and ellipsis
    if (start > 1) {
      pages.push(1)
      if (start > 2) pages.push('...')
    }

    // Add visible pages
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    // Add ellipsis and last page
    if (end < total) {
      if (end < total - 1) pages.push('...')
      pages.push(total)
    }
  }

  return pages
})

// Styles
const buttonBaseClasses = 'flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium transition-all'
const hoverClasses = 'text-subtitle-gray hover:bg-grey-100 hover:text-default-black'
const activeClasses = 'bg-primary text-white'
const disabledClasses = 'text-grey-300 cursor-not-allowed'

// Methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit('update:currentPage', page)
    emit('page-change', page)
  }
}

const handlePerPageChange = (event: Event) => {
  const value = parseInt((event.target as HTMLSelectElement).value)
  emit('update:perPage', value)
  // Reset to first page when changing per page
  emit('update:currentPage', 1)
  emit('page-change', 1)
}
</script>

